// CoachMark: a one-time onboarding callout anchored to a target element.
//
// Design goals:
// - Best-in-class feel: a pulsing highlight ring on the target plus a bubble
//   with a pointer arrow, over a faint dim so it reads clearly.
// - Non-blocking: the whole overlay is pointer-events-none, so the SAME tap
//   that dismisses the mark still reaches whatever is underneath. Tapping the
//   highlighted player both dismisses this mark and opens the player - no
//   awkward "tap once to dismiss, tap again to act".
// - Dismiss on ANY tap anywhere (captured so nothing can swallow it).
//
// It portals to <body> and positions with fixed/viewport coordinates from
// getBoundingClientRect, so it stays aligned even though the target lives
// inside a transformed Vaul drawer or a modal.
//
// Positioning discipline: the mark does NOT render until the target's rect
// has been STABLE for several consecutive frames (first-load layout shift,
// drawer/modal slide-ins). It then enters once with a short ease-out fade in
// its final position, and stays frame-locked to the target afterwards
// (instant per-frame sync - no visible chasing on scroll or late shifts).
import { useEffect, useLayoutEffect, useState } from 'react';
import { createPortal } from 'react-dom';

interface CoachMarkProps {
  /** The element the mark points at. */
  targetRef: React.RefObject<HTMLElement | null>;
  /** Callout copy. */
  text: string;
  /** Called on the first tap anywhere; should mark it seen. */
  onDismiss: () => void;
}

const BUBBLE_MAX_WIDTH = 280;
const GAP = 12; // space between target and bubble

// Frames the target rect must hold still before we show (~200ms at 60fps).
const STABLE_FRAMES_NEEDED = 12;
// Sub-pixel jitter tolerance when comparing rects between frames.
const EPSILON = 0.5;

const rectsDiffer = (a: DOMRect, b: DOMRect) =>
  Math.abs(a.top - b.top) > EPSILON ||
  Math.abs(a.left - b.left) > EPSILON ||
  Math.abs(a.width - b.width) > EPSILON ||
  Math.abs(a.height - b.height) > EPSILON;

export default function CoachMark({ targetRef, text, onDismiss }: CoachMarkProps) {
  const [rect, setRect] = useState<DOMRect | null>(null);
  const [settled, setSettled] = useState(false);
  const [entered, setEntered] = useState(false);

  // Track the target every frame. Before it settles: render nothing (this is
  // what kills the "mark floats up the screen while content loads" effect -
  // the old timer-chain re-measure visibly hopped after the anchor). After it
  // settles: show once, then keep the position synced instantly per frame so
  // scroll and late layout shifts never strand the mark off-target.
  useLayoutEffect(() => {
    let raf = 0;
    let last: DOMRect | null = null;
    let stableFrames = 0;
    const tick = () => {
      const el = targetRef.current;
      if (el) {
        const r = el.getBoundingClientRect();
        if (r.width > 0 && r.height > 0) {
          if (last && !rectsDiffer(r, last)) {
            stableFrames += 1;
          } else {
            stableFrames = 0;
            setRect(r);
          }
          last = r;
          if (stableFrames >= STABLE_FRAMES_NEEDED) setSettled(true);
        }
      }
      raf = requestAnimationFrame(tick);
    };
    raf = requestAnimationFrame(tick);
    return () => cancelAnimationFrame(raf);
  }, [targetRef]);

  const visible = settled && !!rect && rect.width > 0 && rect.height > 0;

  // Single ease-out fade once we first become visible (entrances animate
  // opacity, never position).
  useEffect(() => {
    if (!visible) return;
    const raf = requestAnimationFrame(() => setEntered(true));
    return () => cancelAnimationFrame(raf);
  }, [visible]);

  // Dismiss on the next tap anywhere. Capture phase + once so it fires exactly
  // once, before any element-level handler can stop propagation. Only armed
  // once we actually have a visible target, so a hidden instance (e.g. the
  // mobile-only rows while on a desktop layout) never silently consumes the
  // one-time flag.
  useEffect(() => {
    if (!visible) return;
    const handler = () => onDismiss();
    document.addEventListener('pointerdown', handler, { capture: true, once: true });
    return () => document.removeEventListener('pointerdown', handler, true);
  }, [visible, onDismiss]);

  if (!rect || !visible) return null;

  // Bubble sits below the target by default; flip above if it'd run off-screen.
  const below = rect.bottom + GAP + 90 < window.innerHeight;
  const bubbleLeft = Math.min(Math.max(rect.left, 12), window.innerWidth - BUBBLE_MAX_WIDTH - 12);
  // Arrow points at the target's horizontal centre, relative to the bubble.
  const arrowLeft = Math.min(Math.max(rect.left + rect.width / 2 - bubbleLeft, 18), BUBBLE_MAX_WIDTH - 18);

  return createPortal(
    <div
      className={`fixed inset-0 z-[100] pointer-events-none transition-opacity duration-200 ease-out ${
        entered ? 'opacity-100' : 'opacity-0'
      }`}
    >
      {/* Faint dim so the callout pops without hiding the screen. */}
      <div className="absolute inset-0 bg-black/30" />

      {/* Pulsing highlight ring around the target. */}
      <div
        className="absolute rounded-lg ring-2 ring-accent-yellow animate-pulse motion-reduce:animate-none"
        style={{
          top: rect.top - 5,
          left: rect.left - 5,
          width: rect.width + 10,
          height: rect.height + 10,
        }}
      />

      {/* Callout bubble + arrow. */}
      <div
        className="absolute"
        style={{
          left: bubbleLeft,
          width: BUBBLE_MAX_WIDTH,
          ...(below ? { top: rect.bottom + GAP } : { top: rect.top - GAP - 84 }),
        }}
      >
        {below && (
          <div
            className="w-3 h-3 rotate-45 bg-accent-yellow rounded-[2px] -mb-1.5"
            style={{ marginLeft: arrowLeft - 6 }}
          />
        )}
        <div className="bg-accent-yellow text-primary font-heading font-bold text-sm leading-snug rounded-xl px-4 py-3 shadow-xl shadow-black/40">
          {text}
        </div>
        {!below && (
          <div
            className="w-3 h-3 rotate-45 bg-accent-yellow rounded-[2px] -mt-1.5"
            style={{ marginLeft: arrowLeft - 6 }}
          />
        )}
      </div>
    </div>,
    document.body
  );
}
